import React from 'react';
import { Bell, User, LogOut } from 'lucide-react';
import Footer from '../pages/Footer';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import TranslatedText from '../components/TranslatedText';

function DashboardHeader({ user, handleLogout, title, notificationCount = 0 }) {
  const { t, i18n } = useTranslation();

  return (
    <div>
      <div
        className="w-full bg-white border-b-4 shadow-lg"
        style={{ borderBottomColor: '#B8860B' }}
      >
        {/* Top strip with tricolor elements */}
        <div
          className="w-full h-2"
          style={{
            background:
              'linear-gradient(to right, #FF6600 0%, #FFD700 50%, #FF6600 100%)',
          }}
        ></div>

        {/* Mobile-first responsive header */}
        <div className="px-4 py-3">
          {/* Mobile Layout */}
          <div className="block md:hidden">
            {/* Mobile top row */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <img
                  src="/assets/logos/header1logo.png"
                  alt="Government of India"
                  className="w-auto h-8"
                />
                <img
                  src="/assets/logos/header2logo.png"
                  alt="Government of Maharashtra"
                  className="w-auto h-6"
                />
              </div>
              <div className="flex items-center space-x-2">
                {/* Language Switcher for mobile */}
                <div className="lg:hidden">
                  <LanguageSwitcher />
                </div>
                <div
                  className="flex items-center justify-center rounded-full w-7 h-7"
                  style={{ backgroundColor: '#B8860B' }}
                >
                  <User className="w-4 h-4 text-white" />
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center px-3 py-2 space-x-1 text-xs font-medium text-white transition-colors duration-200 rounded-lg hover:bg-red-600"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  <LogOut className="w-4 h-4" />
                  <span>{t('nav.logout')}</span>
                </button>
              </div>
            </div>

            {/* Mobile center title */}
            <div className="text-center">
              <div className="mb-1 text-lg font-bold text-gray-800">
                {t('dashboard.header.district')}
              </div>
              <div
                className="text-xs font-medium uppercase"
                style={{ color: '#B8860B' }}
              >
                {title} {t('nav.dashboard')}
              </div>
            </div>
          </div>

          {/* Desktop Header */}
          <div className="hidden px-6 py-4 sm:block">
            <div className="flex flex-row items-center w-full">
              {/* Left Logos + District Name */}
              <div className="flex items-center space-x-4">
                {/* Logos */}
                <div className="flex items-center space-x-2">
                  <img
                    src="/assets/logos/header1logo.png"
                    alt="Government of India"
                    className="w-auto h-16"
                  />
                  <img
                    src="/assets/logos/header2logo.png"
                    alt="Government of Maharashtra"
                    className="w-auto h-12"
                  />
                </div>
                <div className="w-px h-12 bg-gray-300"></div>
                {/* District Name */}
                <div
                  className="flex flex-col"
                  style={{
                    fontSize: i18n.language === 'mr' ? '25px' : '25px',
                  }}
                >
                  <div className="text-xl tracking-wide text-gray-800">
                    जिल्हा गडचिरोली
                  </div>
                  <div
                    className="text-2xl font-bold tracking-wide text-gray-800"
                    style={{
                      fontSize: i18n.language === 'mr' ? '25px' : '25px',
                    }}
                  >
                    DISTRICT GADCHIROLI
                  </div>
                </div>
              </div>

              {/* Center Title */}
              <div className="flex-1 text-center">
                {/* Main Title */}
                <div
                  className="font-bold text-black header-title"
                  style={{
                    fontSize: i18n.language === 'mr' ? '50px' : '28px', // manually controlled sizes
                  }}
                >
                  {t('header.pulic.request')}
                </div>
                {/* Subtitle */}
                <div className="text-[11px] sm:text-sm text-gray-800 mt-1 sm:mt-2">
                  {t('header.public.note')}
                </div>
              </div>

              {/* Right User Profile */}
              <div className="flex items-center pl-4 space-x-3 border-l border-gray-300">
                {/* Right section stacked vertically */}
                <div className="flex flex-col items-end gap-4 space-y-2">
                  {/* Language Switcher */}
                  <div className="self-end hidden lg:block">
                    <LanguageSwitcher />
                  </div>

                  {/* User info row */}
                  <div className="flex items-center space-x-3">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full"
                      style={{ backgroundColor: '#B8860B' }}
                    >
                      <User className="w-5 h-5 text-white" />
                    </div>

                    <div className="hidden text-right lg:block">
                      <p className="text-sm font-medium text-gray-900">
                        {i18n.language === 'mr'
                          ? user?.departmentNameMarathi
                          : user?.fullName}
                      </p>
                      <p className="text-xs text-gray-500 capitalize">
                        {user?.role ? (
                          t(`roles.${user.role.toUpperCase()}`, user.role.replace(/_/g, ' '))
                        ) : (
                          t('dashboard.common.officer')
                        )}
                      </p>
                    </div>

                    {/* Logout */}
                    <button
                      onClick={handleLogout}
                      className="flex items-center px-3 py-2 space-x-2 text-sm font-medium text-white transition-colors duration-200 rounded-lg hover:bg-red-600"
                      style={{ backgroundColor: '#dc2626' }}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>{t('nav.logout')}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Bar */}
        <nav
          className="text-white border-t shadow-lg"
          style={{
            background:
              'linear-gradient(135deg, #B8860B 0%, #DAA520 50%, #B8860B 100%)',
            borderTopColor: '#B8860B',
          }}
        >
          <div className="px-4 mx-auto max-w-7xl sm:px-6">
            <div className="flex items-center justify-center h-12 space-x-4 text-xs font-medium tracking-wide uppercase sm:h-14 sm:space-x-8 sm:text-sm">
              <div className="px-2 py-2 text-yellow-100 border-b-2 border-yellow-300 sm:px-4">
                {t('nav.dashboard')}
              </div>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}

export default DashboardHeader;
